import cs from 'classnames';
import { FormikErrors } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Form, Modal, Spinner, Tab, Tabs,
} from 'react-bootstrap';
import { api } from '../../../api';
import { isLastingMemoryEnabled } from '../../../utils/userActionUtils';
import { useMemoryBankForm } from './hooks/useMemoryBankForm';
import classes from './MemoryBank.module.scss';
import { MemoryFormDTO, MemoryScopeType } from './memoryBankTypes';
import { MemoryForm } from './MemoryForm';

type MemoryBankFormProps = {
  onHide: () => void,
  bookId: number,
  userId: number,
}

/**
 * Renders the Memory Bank modal content with two tabs:
 * - "Playthrough Memory": variables that reset on revert (default)
 * - "Lasting Memory": variables that persist across playthroughs
 */
export function MemoryBankForm(props: MemoryBankFormProps) {
  const { onHide, bookId, userId } = props;

  const {
    characters,
    isLoading,
    handleSubmit,
    values,
    errors,
    handleMemoryChange,
    handleMemoryDelete,
    handleMemoryCreate,
    handleMemoryIconAdd,
    memoryIcons,
    usedMemoryIdsInLiveEpisodes,
  } = useMemoryBankForm(bookId, userId, onHide);

  // Lasting-memory access: admin OR per-user grant OR global Settings flag.
  // We fetch settings once on mount; the per-user check is sync (auth context).
  // Defaults to "disabled" until settings resolve so the tab doesn't flicker on.
  const [settings, setSettings] = useState<Record<string, Record<string, { value?: string }>> | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    api.get('/v1/settings').then((res: { data?: { settings?: Record<string, Record<string, { value?: string }>> } }) => {
      if (!cancelled) setSettings(res?.data?.settings);
    }).catch(() => {
      if (!cancelled) setSettings({});
    });
    return () => { cancelled = true; };
  }, []);
  const lastingMemoryEnabled = isLastingMemoryEnabled(undefined, settings);

  /** Partition variables by scope type for tabbed display */
  const playthroughMemories: Array<{ dto: MemoryFormDTO; originalIndex: number }> = useMemo(
    () => values.memoryBankSlots
      .map((dto, index) => ({ dto, originalIndex: index }))
      .filter(({ dto }) => dto.scopeType !== MemoryScopeType.Game),
    [values.memoryBankSlots],
  );

  const lastingMemories: Array<{ dto: MemoryFormDTO; originalIndex: number }> = useMemo(
    () => values.memoryBankSlots
      .map((dto, index) => ({ dto, originalIndex: index }))
      .filter(({ dto }) => dto.scopeType === MemoryScopeType.Game),
    [values.memoryBankSlots],
  );

  const renderMemoryList = (
    memories: Array<{ dto: MemoryFormDTO; originalIndex: number }>,
  ) => memories.map(({ dto, originalIndex }) => {
    const memoryFieldErrors = errors.memoryBankSlots?.[originalIndex] ?? {};
    const isLocked = dto.lockedAt != null || usedMemoryIdsInLiveEpisodes.includes(dto.id);
    return (
      <MemoryForm
        key={dto.id}
        memoryIndex={originalIndex}
        memoryFormDTO={dto}
        onChange={handleMemoryChange}
        onDelete={handleMemoryDelete}
        errors={memoryFieldErrors as FormikErrors<MemoryFormDTO>}
        characters={characters ?? []}
        memoryIcons={memoryIcons ?? []}
        onIconAdd={handleMemoryIconAdd}
        isLocked={isLocked}
      />
    );
  });

  return (
    <>
      <Form>
        <Modal.Header closeButton>
          <Modal.Title>
            Memory Bank
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className={classes.memoryBankModalBody}>
          <Tabs defaultActiveKey="playthrough" id="memory-bank-scope-tabs">
            <Tab eventKey="playthrough" title="Playthrough Memories">
              <div className={classes.memoryTabContent}>
                <div className={classes.memoryTabHeader}>
                  <p className={classes.memoryTabDescription}>
                    These values reset when a reader reverts to a save point.
                  </p>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleMemoryCreate(MemoryScopeType.Playthrough)}
                    className={classes.memoryBankAddSlotButton}
                  >
                    + Add memory
                  </Button>
                </div>
                {renderMemoryList(playthroughMemories)}
              </div>
            </Tab>
            {lastingMemoryEnabled && (
              <Tab eventKey="lasting" title="Lasting Memories">
                <div className={classes.memoryTabContent}>
                  <div className={classes.memoryTabHeader}>
                    <p className={classes.memoryTabDescription}>
                      These values carry over between playthroughs.
                    </p>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleMemoryCreate(MemoryScopeType.Game)}
                      className={classes.memoryBankAddSlotButton}
                    >
                      + Add memory
                    </Button>
                  </div>
                  {renderMemoryList(lastingMemories)}
                </div>
              </Tab>
            )}
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            type="button"
            variant="secondary"
            onClick={onHide}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            type="button"
            variant="primary"
            disabled={isLoading}
            onClick={() => handleSubmit()}
          >
            Save
          </Button>
        </Modal.Footer>
      </Form>
      {isLoading
        && (
          <>
            <div className={cs({ overlay: isLoading })} />
            <Spinner
              variant="primary"
              animation="border"
              className="spinner"
            />
          </>
        )}
    </>
  );
}
